/* eslint-disable no-restricted-syntax */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphqlClient } from "@/graphql/client";

const ME_QUERY = `query { me { id email name slug role plan emailVerified createdAt } }`;
const LOGOUT_MUTATION = `mutation { logout }`;

interface User {
  id: string;
  email: string;
  name: string | null;
  slug: string;
  role: string;
  plan: string;
  emailVerified: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  refetch: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: () => graphqlClient.request<{ me: User | null }>(ME_QUERY),
    retry: false,
    staleTime: Infinity,
  });

  const logoutMutation = useMutation({
    mutationFn: () => graphqlClient.request(LOGOUT_MUTATION),
    onSuccess: () => {
      queryClient.clear();
      window.location.href = "/login";
    },
  });

  const user = data?.me ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        logout: () => {
          logoutMutation.mutate();
        },
        refetch: async () => {
          const result = await refetch();
          return result.data?.me ?? null;
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
